// Branded Open Graph image map.
// All images are 1216x640 (≈ 1.9:1, the OG/Twitter recommended ratio),
// dark navy + gold typography to match site brand.
//
// Usage in route head():
//   import { ogImageFor } from "@/lib/ogImages";
//   const ogImage = ogImageFor({ service: "seo" });           // hub
//   const ogImage = ogImageFor({ city: "denver" });           // city hub
//   const ogImage = ogImageFor({ service: "seo", city: "denver" }); // spoke (uses city image)
//
// In the meta array:
//   { property: "og:image", content: `${SITE_URL}${ogImage}` },
//   { property: "og:image:width", content: "1216" },
//   { property: "og:image:height", content: "640" },
//   { name: "twitter:image", content: `${SITE_URL}${ogImage}` },
//   { name: "twitter:card", content: "summary_large_image" },

import ogDefault from "@/assets/og/og-default.jpg";
import ogSeo from "@/assets/og/og-seo.jpg";
import ogWebDesign from "@/assets/og/og-web-design.jpg";
import ogGoogleMaps from "@/assets/og/og-google-maps.jpg";
import ogPpc from "@/assets/og/og-ppc.jpg";
import ogSocialMedia from "@/assets/og/og-social-media.jpg";
import ogGeoAi from "@/assets/og/og-geo-ai.jpg";

import ogColoradoSprings from "@/assets/og/og-city-colorado-springs.jpg";
import ogDenver from "@/assets/og/og-city-denver.jpg";
import ogPueblo from "@/assets/og/og-city-pueblo.jpg";
import ogCastleRock from "@/assets/og/og-city-castle-rock.jpg";
import ogMonument from "@/assets/og/og-city-monument.jpg";
import ogFortCollins from "@/assets/og/og-city-fort-collins.jpg";
import ogBoulder from "@/assets/og/og-city-boulder.jpg";
import ogParker from "@/assets/og/og-city-parker.jpg";

// Per-category blog/article OG images (one per ArticleCategory in learningHub).
import ogBlogSeo from "@/assets/og/og-blog-seo.jpg";
import ogBlogWebDesign from "@/assets/og/og-blog-web-design.jpg";
import ogBlogGoogleMaps from "@/assets/og/og-blog-google-maps.jpg";
import ogBlogAiGeo from "@/assets/og/og-blog-ai-geo.jpg";
import ogBlogPricing from "@/assets/og/og-blog-pricing.jpg";
import ogBlogBuyersGuide from "@/assets/og/og-blog-buyers-guide.jpg";
import ogBlogReviews from "@/assets/og/og-blog-reviews.jpg";
import ogBlogMarketing from "@/assets/og/og-blog-marketing-strategy.jpg";

export const OG_IMAGE_WIDTH = 1216;
export const OG_IMAGE_HEIGHT = 640;

export type OgServiceKey =
  | "seo"
  | "web-design"
  | "google-maps"
  | "ppc"
  | "social-media"
  | "geo-ai";

export type OgCityKey =
  | "colorado-springs"
  | "denver"
  | "pueblo"
  | "castle-rock"
  | "monument"
  | "fort-collins"
  | "boulder"
  | "parker";

/** Mirrors ArticleCategory in learningHub.ts. Kept as a string union here
 *  to avoid a circular import (learningHub also imports from this file's
 *  consumers).  When categories are added, mirror them in BOTH places. */
export type OgArticleCategory =
  | "Pricing & Budget"
  | "Web Design"
  | "SEO"
  | "Google Maps"
  | "AI & GEO"
  | "Buyer's Guide"
  | "Reviews & Reputation"
  | "Marketing Strategy";

const SERVICE_OG: Record<OgServiceKey, string> = {
  seo: ogSeo,
  "web-design": ogWebDesign,
  "google-maps": ogGoogleMaps,
  ppc: ogPpc,
  "social-media": ogSocialMedia,
  "geo-ai": ogGeoAi,
};

const CITY_OG: Record<OgCityKey, string> = {
  "colorado-springs": ogColoradoSprings,
  denver: ogDenver,
  pueblo: ogPueblo,
  "castle-rock": ogCastleRock,
  monument: ogMonument,
  "fort-collins": ogFortCollins,
  boulder: ogBoulder,
  parker: ogParker,
};

const ARTICLE_OG: Record<OgArticleCategory, string> = {
  SEO: ogBlogSeo,
  "Web Design": ogBlogWebDesign,
  "Google Maps": ogBlogGoogleMaps,
  "AI & GEO": ogBlogAiGeo,
  "Pricing & Budget": ogBlogPricing,
  "Buyer's Guide": ogBlogBuyersGuide,
  "Reviews & Reputation": ogBlogReviews,
  "Marketing Strategy": ogBlogMarketing,
};

/**
 * Returns the asset path for a route's OG image.
 * Priority: article category > city image > service image > default.
 */
export function ogImageFor(opts: {
  service?: OgServiceKey;
  city?: OgCityKey;
  articleCategory?: OgArticleCategory;
}): string {
  if (opts.articleCategory && ARTICLE_OG[opts.articleCategory])
    return ARTICLE_OG[opts.articleCategory];
  if (opts.city && CITY_OG[opts.city]) return CITY_OG[opts.city];
  if (opts.service && SERVICE_OG[opts.service]) return SERVICE_OG[opts.service];
  return ogDefault;
}

export const OG_DEFAULT = ogDefault;

/**
 * Build the standard OG + Twitter meta tags for a route.
 * Returns an array ready to spread into the route's `meta` array.
 */
export function ogMetaTags(absoluteImageUrl: string) {
  return [
    { property: "og:image", content: absoluteImageUrl },
    { property: "og:image:width", content: String(OG_IMAGE_WIDTH) },
    { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: absoluteImageUrl },
  ];
}

/**
 * Generate punchier Twitter-specific title + description meta tags.
 * Twitter previews are smaller and shown in a faster-scrolling feed, so we
 * tighten the copy and lead with a benefit/CTA when possible.
 *
 * Title cap: 70 chars (Twitter truncates ~70). Description cap: 200 chars.
 */
export function twitterTextTags(opts: { title: string; description: string }) {
  return [
    { name: "twitter:title", content: clamp(opts.title, 70) },
    { name: "twitter:description", content: clamp(opts.description, 200) },
  ];
}

function clamp(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return `${slice.slice(0, lastSpace > 40 ? lastSpace : slice.length).trimEnd()}…`;
}
